// src/pages/GiftCards.jsx
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Search, X, ArrowLeft, Gift, Copy, CheckCheck, Mail, Loader2,
  ExternalLink, ShieldCheck, RefreshCw, AlertTriangle,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../api/api";
import { useWallet } from "../context/WalletContext";

const naira = (n) => `₦${Number(n || 0).toLocaleString("en-NG")}`;

const STATUS = {
  completed:  { label: "Delivered",  cls: "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800" },
  processing: { label: "Processing", cls: "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800" },
  failed:     { label: "Refunded",   cls: "bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700" },
};

const inputCls =
  "w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 outline-none transition";

function Logo({ src, name, className = "" }) {
  const [bad, setBad] = useState(false);
  if (!src || bad) {
    return (
      <div className={`flex items-center justify-center bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-bold ${className}`}>
        {(name || "?")[0]}
      </div>
    );
  }
  return <img src={src} alt={name} loading="lazy" onError={() => setBad(true)} className={`object-contain ${className}`} />;
}

function CopyRow({ label, value }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  const copy = () =>
    navigator.clipboard.writeText(value).then(() => {
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    });
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
        <p className="font-mono text-sm text-gray-900 dark:text-white break-all">{value}</p>
      </div>
      <button
        onClick={copy}
        className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${
          done
            ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
            : "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 hover:bg-purple-100"
        }`}
      >
        {done ? <CheckCheck size={13} /> : <Copy size={13} />}
        {done ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

// ── Shows the codes for one order (polls while it is still processing) ──────
function OrderCards({ orderId, onLoaded }) {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let stop = false;
    let t;
    let errors = 0;
    const tick = async () => {
      try {
        const res = await api.get(`/giftcards/orders/${orderId}`);
        if (stop) return;
        setData(res.data);
        setFailed(false);
        if (res.data.order.status === "processing") t = setTimeout(tick, 6000);
        else if (onLoaded) onLoaded(res.data.order);
      } catch (e) {
        if (stop || e.code === "ERR_CANCELED") return;
        errors += 1;
        if (errors < 5) t = setTimeout(tick, 8000);
        else setFailed(true);
      }
    };
    tick();
    return () => { stop = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const resend = async () => {
    setSending(true);
    try {
      const res = await api.post(`/giftcards/orders/${orderId}/resend-email`);
      toast.success(res.data.message || "Email sent");
    } catch { /* message already shown */ }
    finally { setSending(false); }
  };

  if (failed) return <p className="text-sm text-red-500 py-4">Could not load this gift card. Please refresh.</p>;
  if (!data) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-purple-500" /></div>;

  const { order, cards } = data;

  if (order.status === "processing") {
    return (
      <div className="text-center py-6">
        <Loader2 className="mx-auto mb-3 animate-spin text-purple-500" />
        <p className="text-sm font-medium text-gray-900 dark:text-white">Preparing your gift card…</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          This page updates automatically. You can also leave and find it under My Gift Cards.
        </p>
      </div>
    );
  }
  if (order.status === "failed") {
    return (
      <div className="text-center py-6">
        <p className="text-sm font-medium text-gray-900 dark:text-white">This order could not be completed.</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{naira(order.priceNgn)} has been returned to your wallet.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start gap-2 p-3 mb-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <ShieldCheck size={14} className="text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
          Keep these details private. Anyone with the code can use the card. We will never ask you for it.
        </p>
      </div>

      {cards.length === 0 && <p className="text-sm text-gray-500">Card details are not available. Please contact support.</p>}

      {cards.map((c, i) => (
        <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-4 py-2 mb-3">
          {cards.length > 1 && <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 pt-1">Card {i + 1}</p>}
          <CopyRow label="Card number" value={c.cardNumber} />
          <CopyRow label="PIN" value={c.pin} />
          <CopyRow label="Code" value={c.code} />
          {c.url && (
            <a href={c.url} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-sm text-purple-600 dark:text-purple-400 py-2 hover:underline">
              <ExternalLink size={14} /> Open redeem link
            </a>
          )}
          {c.expiry && <p className="text-xs text-gray-500 dark:text-gray-400 pb-2">Expires: {c.expiry}</p>}
          {c.image && <img src={c.image} alt="Gift card" className="max-w-full rounded-lg my-2" />}
        </div>
      ))}

      <button
        onClick={resend}
        disabled={sending}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-60"
      >
        <Mail size={14} /> {sending ? "Sending…" : order.emailSentAt ? "Email it to me again" : "Email it to me"}
      </button>
    </div>
  );
}

// ── Buy panel (bottom sheet on mobile, centered on desktop) ─────────────────
// Rendered through a portal above everything (including the support button),
// and the page behind it is locked from scrolling while it is open.
function BuyPanel({ brand, onClose, onBought }) {
  const { walletBalance, refreshWallet } = useWallet();
  const [country, setCountry] = useState(brand.countries.length === 1 ? brand.countries[0].iso : "");
  const [products, setProducts] = useState([]);
  const [loadingP, setLoadingP] = useState(false);
  const [sel, setSel] = useState(null);       // { productId, amount, currency, instructions }
  const [custom, setCustom] = useState("");
  const [price, setPrice] = useState(null);   // the final price the user pays
  const [refining, setRefining] = useState(false);
  const [step, setStep] = useState("pick");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [order, setOrder] = useState(null);
  const reqId = useRef(null);
  const seq = useRef(0);

  // lock page scroll while the panel is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (!country) return;
    let off = false;
    setLoadingP(true); setProducts([]); setSel(null); setCustom(""); setPrice(null);
    api.get("/giftcards/products", { params: { brandId: brand.brandId, country } })
      .then((r) => { if (!off) setProducts(r.data.products || []); })
      .catch(() => {})
      .finally(() => { if (!off) setLoadingP(false); });
    return () => { off = true; };
  }, [country, brand.brandId]);

  // Any-amount cards: the price shows instantly, the exact figure is confirmed quietly in the background
  useEffect(() => {
    if (!sel || !sel.range) return;
    const my = ++seq.current;
    setRefining(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/giftcards/quote", { productId: sel.productId, amount: Number(sel.amount) });
        if (my === seq.current) setPrice(r.data.priceNgn);
      } catch { /* the estimate stays; the server re-checks at payment */ }
      finally { if (my === seq.current) setRefining(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [sel?.productId, sel?.amount, sel?.range]); // eslint-disable-line react-hooks/exhaustive-deps

  const fixed = products
    .filter((p) => p.type === "FIXED")
    .flatMap((p) => p.options.map((o) => ({ productId: p.productId, amount: o.amount, priceNgn: o.priceNgn, currency: p.currency, instructions: p.instructions })))
    .sort((a, b) => a.amount - b.amount);
  const ranges = products.filter((p) => p.type === "RANGE");

  const goConfirm = () => {
    reqId.current = crypto.randomUUID();
    setErr("");
    setStep("confirm");
  };

  // one tap on a fixed amount goes straight to the confirm screen
  const pickFixed = (o) => {
    setCustom("");
    setSel({ ...o, range: false });
    setPrice(o.priceNgn);
    setRefining(false);
    goConfirm();
  };

  const onCustom = (p, v) => {
    setCustom(v);
    const n = Number(v);
    if (p.currency === "NGN") {
      // the number typed is what the user pays; work out the card value that fits inside it
      const face = Math.floor(n / p.perUnit);
      if (v !== "" && face >= p.min && face <= p.max) {
        setSel({ productId: p.productId, amount: face, currency: p.currency, instructions: p.instructions, range: true });
        setPrice(Math.ceil(face * p.perUnit));
      } else {
        setSel(null);
        setPrice(null);
      }
      return;
    }
    if (v !== "" && n >= p.min && n <= p.max && Math.round(n * 100) === n * 100) {
      setSel({ productId: p.productId, amount: n, currency: p.currency, instructions: p.instructions, range: true });
      setPrice(Math.ceil(n * p.perUnit));
    } else {
      setSel(null);
      setPrice(null);
    }
  };

  const enough = price != null && walletBalance >= price;

  const buy = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(
        "/giftcards/buy",
        { productId: sel.productId, amount: Number(sel.amount), expectedPriceNgn: price, clientRequestId: reqId.current },
        { timeout: 90000 }
      );
      refreshWallet?.();
      setOrder(r.data.order);
      setStep("done");
      onBought?.();
    } catch (e) {
      if (e.code === "ERR_CANCELED") { setBusy(false); return; }
      const d = e.response?.data;
      if (d?.code === "PRICE_CHANGED") {
        setPrice(d.priceNgn);
        reqId.current = crypto.randomUUID();
        setErr(`The price is now ${naira(d.priceNgn)}. Please review and confirm again.`);
      } else if (!e.response) {
        setErr("No response from the server. Your order may still be processing. Check My Gift Cards before trying again.");
      } else {
        if (d?.order) refreshWallet?.();
        setErr(d?.message || "Purchase failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const countryName = brand.countries.find((c) => c.iso === country)?.name;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center"
      style={{ zIndex: 2147483000 }}
      onClick={step === "pick" ? onClose : undefined}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-lg max-h-[92vh] flex flex-col rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl"
      >
        {/* header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
          {step === "confirm" && (
            <button onClick={() => setStep("pick")} disabled={busy} className="p-1 text-gray-500"><ArrowLeft size={18} /></button>
          )}
          <Logo src={brand.logoUrl} name={brand.name} className="w-9 h-9 rounded-lg" />
          <p className="flex-1 font-semibold text-gray-900 dark:text-white truncate">{brand.name}</p>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-800 dark:hover:text-white"><X size={20} /></button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {step === "pick" && (
            <>
              {brand.countries.length > 1 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Country</label>
                  <select value={country} onChange={(e) => setCountry(e.target.value)} className={inputCls}>
                    <option value="">Select a country</option>
                    {brand.countries.map((c) => <option key={c.iso} value={c.iso}>{c.name}</option>)}
                  </select>
                </div>
              )}

              {!country && <p className="text-sm text-gray-500 text-center py-6">Choose a country to see the available cards.</p>}

              {country && loadingP && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[...Array(6)].map((_, i) => <div key={i} className="h-[58px] rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
                </div>
              )}

              {country && !loadingP && products.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No cards available for this country right now.</p>
              )}

              {fixed.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Tap an amount to continue</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {fixed.map((o) => (
                      <button
                        key={`${o.productId}-${o.amount}`}
                        onClick={() => pickFixed(o)}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-left hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 active:scale-[0.98] transition"
                      >
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{o.amount} {o.currency}</p>
                        <p className="text-xs font-semibold text-purple-600 dark:text-purple-400">{naira(o.priceNgn)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {ranges.map((p) => {
                const isNgn = p.currency === "NGN";
                const minPay = Math.ceil(p.min * p.perUnit);
                const maxPay = Math.floor(p.max * p.perUnit);
                return (
                  <div key={p.productId} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                      {isNgn
                        ? `How much do you want to pay? (${naira(minPay)} – ${naira(maxPay)})`
                        : `Enter any amount (${p.min} – ${p.max} ${p.currency})`}
                    </p>
                    <div className="relative">
                      <input
                        type="number" inputMode="decimal" step={isNgn ? "1" : "0.01"}
                        value={custom}
                        onChange={(e) => onCustom(p, e.target.value)}
                        placeholder={isNgn ? `e.g. ${Math.max(minPay, 1000)}` : `e.g. ${Math.min(Math.max(p.min, 25), p.max)}`}
                        className={`${inputCls} pr-16`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500">
                        {isNgn ? "₦" : p.currency}
                      </span>
                    </div>
                    {custom !== "" && !sel && (
                      <p className="text-xs text-red-500 mt-1.5">
                        {isNgn
                          ? Number(custom) < minPay
                            ? `Minimum is ${naira(minPay)}. Please increase the amount.`
                            : `Maximum is ${naira(maxPay)}. Please reduce the amount.`
                          : `Enter an amount between ${p.min} and ${p.max}.`}
                      </p>
                    )}
                  </div>
                );
              })}

              {sel?.instructions && (
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{sel.instructions}</p>
              )}
            </>
          )}

          {step === "confirm" && sel && (
            <>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                {[
                  ["Gift card", brand.name],
                  ["Country", countryName],
                  ["Card value", `${sel.amount} ${sel.currency}`],
                  ["Wallet balance", naira(walletBalance)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between px-4 py-2.5 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{k}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{v}</span>
                  </div>
                ))}
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className="font-semibold text-gray-900 dark:text-white">You pay</span>
                  <span className="font-bold text-purple-600 dark:text-purple-400">{naira(price)}</span>
                </div>
              </div>

              {price != null && !enough && (
                <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                  Your wallet balance is not enough. <Link to="/dashboard/wallet" className="underline font-semibold">Fund wallet</Link>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                Your code appears on this screen and is emailed to you. If anything fails, your wallet is refunded automatically.
              </p>

              {err && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <AlertTriangle size={14} className="text-red-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-700 dark:text-red-300">{err}</p>
                </div>
              )}
            </>
          )}

          {step === "done" && order && (
            <>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                  <CheckCheck className="text-green-600" />
                </div>
                <p className="font-semibold text-gray-900 dark:text-white">Order placed</p>
              </div>
              <OrderCards orderId={order.id} />
            </>
          )}
        </div>

        {/* sticky action bar */}
        {step === "pick" && sel?.range && price != null && (
          <div className="shrink-0 p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-b-2xl">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-600 dark:text-gray-300">You pay</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">{naira(price)}</span>
            </div>
            <button onClick={goConfirm} className="w-full py-3 rounded-xl text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 active:bg-purple-800 transition">
              Continue
            </button>
          </div>
        )}

        {step === "confirm" && (
          <div className="shrink-0 p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-b-2xl">
            <button
              onClick={buy}
              disabled={busy || !enough || refining}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
            >
              {busy ? <><Loader2 size={15} className="animate-spin" /> Processing…</> : `Pay ${naira(price)}`}
            </button>
          </div>
        )}

        {step === "done" && (
          <div className="shrink-0 p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-b-2xl">
            <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              Done
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── My Gift Cards ───────────────────────────────────────────────────────────
function MyCards({ refreshKey }) {
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/giftcards/orders");
      setOrders(r.data.orders || []);
    } catch (e) {
      if (e.code !== "ERR_CANCELED") setOrders((o) => o || []);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (!orders?.some((o) => o.status === "processing")) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [orders, load]);

  if (!orders) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-purple-500" /></div>;
  if (orders.length === 0) {
    return (
      <div className="text-center py-16 px-6">
        <Gift className="mx-auto mb-3 text-purple-400" size={30} />
        <p className="text-sm font-semibold text-gray-900 dark:text-white">No gift cards yet</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Cards you buy will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {orders.map((o) => {
        const st = STATUS[o.status] || STATUS.processing;
        return (
          <div key={o.id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <button onClick={() => setOpen(open === o.id ? null : o.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{o.brandName}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {o.faceValue} {o.currency} · {o.countryName} · {new Date(o.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold text-purple-600 dark:text-purple-400">{naira(o.priceNgn)}</p>
                <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full border text-[10px] font-medium ${st.cls}`}>{st.label}</span>
              </div>
            </button>
            {open === o.id && (
              <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800 pt-3">
                <OrderCards orderId={o.id} onLoaded={load} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function GiftCards() {
  const [tab, setTab] = useState("buy");
  const [state, setState] = useState({ loading: true, error: "", enabled: true, notice: "", brands: [] });
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [brand, setBrand] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadBrands = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await api.get("/giftcards/brands");
      setState({ loading: false, error: "", enabled: r.data.enabled, notice: r.data.notice || "", brands: r.data.brands || [] });
    } catch (e) {
      if (e.code === "ERR_CANCELED") return;
      setState((s) => ({ ...s, loading: false, error: "Could not load gift cards. Please try again." }));
    }
  }, []);

  useEffect(() => { loadBrands(); }, [loadBrands]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(state.brands.map((b) => b.category).filter(Boolean))).sort()],
    [state.brands]
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.brands.filter(
      (b) => (category === "All" || b.category === category) && (!q || b.name.toLowerCase().includes(q))
    );
  }, [state.brands, query, category]);

  return (
    <div className="pb-10">
      <div className="pt-1 pb-5 mb-4 border-b border-gray-100 dark:border-gray-800 text-center">
        <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] text-purple-600 dark:text-purple-400 mb-2">Gift Cards</p>
        <h1 className="font-serif text-xl sm:text-3xl font-normal text-gray-900 dark:text-white leading-snug mb-2">
          Buy gift cards in naira
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Paid from your wallet. Delivered here and to your email.</p>
      </div>

      <div className="flex gap-2 mb-4 max-w-md mx-auto">
        {[["buy", "Buy"], ["mine", "My Gift Cards"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${
              tab === k
                ? "bg-purple-600 text-white"
                : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "mine" && <MyCards refreshKey={refreshKey} />}

      {tab === "buy" && (
        <>
          {state.notice && (
            <div className="mb-4 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
              {state.notice}
            </div>
          )}

          {state.loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {[...Array(10)].map((_, i) => <div key={i} className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />)}
            </div>
          ) : state.error ? (
            <div className="text-center py-14">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{state.error}</p>
              <button onClick={loadBrands} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium">
                <RefreshCw size={14} /> Try again
              </button>
            </div>
          ) : !state.enabled ? (
            <div className="text-center py-14">
              <Gift className="mx-auto mb-3 text-purple-400" size={30} />
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Gift cards are temporarily unavailable</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Please check back soon.</p>
            </div>
          ) : (
            <>
              <div className="relative mb-3">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search brands (Amazon, Apple, Steam…)" className={`${inputCls} pl-9`} />
              </div>

              <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
                      category === c
                        ? "bg-purple-600 text-white"
                        : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              {shown.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-12">No brands match your search.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {shown.map((b) => (
                    <button
                      key={b.brandId}
                      onClick={() => setBrand(b)}
                      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 text-center hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-sm transition"
                    >
                      <Logo src={b.logoUrl} name={b.name} className="w-14 h-14 mx-auto rounded-xl mb-2" />
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{b.name}</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {b.countries.length} {b.countries.length === 1 ? "country" : "countries"}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {brand && (
        <BuyPanel
          brand={brand}
          onClose={() => setBrand(null)}
          onBought={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}